// src/components/requests/RequestForm.jsx
import React, { useState, useCallback } from 'react';

export const AppView = {
  HOME: 'HOME',
  RESULTS: 'RESULTS',
  PROVIDER: 'PROVIDER',
  USER_DASHBOARD: 'USER_DASHBOARD',
};

export const TIMEFRAME_OPTIONS = [
  { value: 'asap', label: 'Urgent (ASAP)' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'flexible', label: 'Flexible' },
];

export const PROVIDER_CATEGORIES = [
  'Home Improvement & Repair',
  'Cleaning Services',
  'Plumbing & HVAC',
  'Electrical',
  'Moving & Storage',
  'Web & Graphic Design',
  'Digital Marketing',
  'Tutoring & Education',
  'Events & Catering',
  'Personal Training & Wellness',
  'Legal & Financial',
  'Other'
];

const inferCategoryFromDescription = (text = '') => {
  const value = String(text).toLowerCase();
  if (!value) return 'General';
  if (/plumb|leak|electric|repair|hvac|clean|moving|paint/.test(value)) return 'Home Services';
  if (/design|brand|website|marketing|content|video|creative/.test(value)) return 'Creative';
  if (/legal|finance|account|coach|trainer|tutor|consult/.test(value)) return 'Professional';
  return 'General';
};

const RequestForm = ({ onSubmit, isSubmitting, isDarkMode, bookData, placeholder }) => {
  const [description, setDescription] = useState('');
  const [maxResponses, setMaxResponses] = useState(bookData?.requestsSettings?.defaultMaxResponses || 3);
  const [timeframe, setTimeframe] = useState(TIMEFRAME_OPTIONS[0]?.value || 'asap');
  const [location, setLocation] = useState('');
  const [manualCategory, setManualCategory] = useState('');
  const [showIntentConfirm, setShowIntentConfirm] = useState(false);
  const [intentSummary, setIntentSummary] = useState('');
  const [mustHaves, setMustHaves] = useState('');
  const [dealBreakers, setDealBreakers] = useState('');

  const startIntentConfirmation = useCallback((e) => {
    e.preventDefault();
    if (!description.trim()) return;
    setIntentSummary(description.trim());
    setShowIntentConfirm(true);
  }, [description]);

  const submitConfirmedIntent = useCallback(() => {
    if (!description.trim() || !intentSummary.trim()) return;

    const category = manualCategory || inferCategoryFromDescription(description);
    onSubmit({
      description,
      maxResponses,
      timeframe,
      location,
      category,
      intentConfirmation: {
        summary: intentSummary,
        mustHaves,
        dealBreakers
      }
    });
  }, [description, maxResponses, timeframe, location, manualCategory, intentSummary, mustHaves, dealBreakers, onSubmit]);

  const submitDirectRequest = useCallback(() => {
    if (!description.trim()) return;

    const category = manualCategory || inferCategoryFromDescription(description);
    onSubmit({
      description,
      maxResponses,
      timeframe,
      location,
      category,
      intentConfirmation: {
        summary: description.trim(),
        mustHaves: '',
        dealBreakers: '',
        mode: 'direct'
      }
    });
  }, [description, maxResponses, timeframe, location, manualCategory, onSubmit]);

  const primaryColor = bookData?.styling?.primaryColor || '#6366f1';
  const accentColor = bookData?.styling?.accentColor || '#8b5cf6';

  return (
    <div className="w-full max-w-4xl mx-auto animate-fade-in-up flex flex-col items-center">
      {!bookData?.title && (
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white mb-4">
            Stop searching. <span
              className="text-transparent bg-clip-text bg-gradient-to-r"
              style={{
                backgroundImage: `linear-gradient(to right, ${primaryColor}, ${accentColor})`
              }}
            >
              State the outcome
            </span>
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            The Request Agent structures your goal, finds suitable providers, and starts optimizing for the best solution.
          </p>
        </div>
      )}

      <div className="w-full max-w-3xl">
        <form onSubmit={startIntentConfirmation}>
          <div className={`rounded-2xl shadow-2xl border transition-colors duration-300 ${
            isDarkMode
              ? 'bg-slate-800/50 border-slate-700'
              : 'bg-white'
          }`}
          style={!isDarkMode ? {
            border: primaryColor
          } : {}}
          >
            <div className="p-3 sm:p-4">
              <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}>
                What outcome do you want?
              </label>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setShowIntentConfirm(false);
                }}
                placeholder={placeholder || 'E.g., I need my bathroom leak fixed tomorrow morning with a reliable plumber who can supply parts.'}
                rows={3}
                className={`w-full px-0 py-2 border-none focus:ring-0 outline-none resize-none text-md placeholder-slate-400 transition-colors ${
                  isDarkMode
                    ? 'bg-transparent text-white placeholder-slate-500'
                    : 'bg-transparent text-slate-900 placeholder-slate-400'
                }`}
                required
              />
            </div>

            <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}></div>

            <div className="p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-12 gap-4">
              <div className="sm:col-span-2 relative">
                <select
                  value={manualCategory}
                  onChange={(e) => setManualCategory(e.target.value)}
                  className={`w-full px-3 py-3 rounded-xl border focus:ring-2 focus:border-transparent outline-none text-sm appearance-none cursor-pointer transition-all ${
                    isDarkMode
                      ? 'bg-slate-900/50 border-slate-700 text-white focus:ring-indigo-500'
                      : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-indigo-500'
                  }`}
                  style={{ '--tw-ring-color': primaryColor }}
                >
                  <option value="">Auto</option>
                  <option value="Home Services">Home Services</option>
                  <option value="Professional">Professional</option>
                  <option value="Creative">Creative</option>
                  <option value="General">General</option>
                </select>
                <label className={`absolute -top-2 left-2 px-1 text-[10px] font-bold uppercase tracking-wider ${
                  isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-400'
                }`}>
                  Category
                </label>
              </div>

              <div className="sm:col-span-3 relative">
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={maxResponses}
                  onChange={(e) => setMaxResponses(Number(e.target.value))}
                  className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent outline-none transition-all text-sm ${
                    isDarkMode
                      ? 'bg-slate-900/50 border-slate-700 text-white placeholder-slate-500 focus:ring-indigo-500'
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500'
                  }`}
                  style={{ '--tw-ring-color': primaryColor }}
                />
                <label className={`absolute -top-2 left-2 px-1 text-[10px] font-bold uppercase tracking-wider ${
                  isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-400'
                }`}>
                  Max Offers
                </label>
              </div>

              <div className="sm:col-span-4 relative">
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="City or Area"
                  className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent outline-none transition-all text-sm ${
                    isDarkMode
                      ? 'bg-slate-900/50 border-slate-700 text-white placeholder-slate-500 focus:ring-indigo-500'
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-indigo-500'
                  }`}
                  style={{ '--tw-ring-color': primaryColor }}
                />
                <label className={`absolute -top-2 left-2 px-1 text-[10px] font-bold uppercase tracking-wider ${
                  isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-400'
                }`}>
                  Location
                </label>
              </div>

              <div className="sm:col-span-3 relative">
                <select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:border-transparent outline-none text-sm appearance-none cursor-pointer transition-all ${
                    isDarkMode
                      ? 'bg-slate-900/50 border-slate-700 text-white focus:ring-indigo-500'
                      : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-indigo-500'
                  }`}
                  style={{ '--tw-ring-color': primaryColor }}
                >
                  {TIMEFRAME_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <label className={`absolute -top-2 left-2 px-1 text-[10px] font-bold uppercase tracking-wider ${
                  isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-400'
                }`}>
                  Timeframe
                </label>
                <div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-500'
                }`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div className="sm:col-span-12 flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={submitDirectRequest}
                  disabled={isSubmitting || !description.trim()}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                    isSubmitting || !description.trim()
                      ? isDarkMode
                        ? 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-50'
                        : 'bg-slate-300 text-slate-500 cursor-not-allowed opacity-50'
                      : isDarkMode
                        ? 'bg-slate-700 text-white hover:bg-slate-600'
                        : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                  }`}
                >
                  Send Now
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !description.trim()}
                  className={`px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 flex items-center gap-2 ${
                    isSubmitting || !description.trim()
                      ? isDarkMode
                        ? 'bg-slate-700 cursor-not-allowed opacity-50'
                        : 'bg-slate-300 cursor-not-allowed opacity-50'
                      : 'shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                  }`}
                  style={!(isSubmitting || !description.trim()) ? {
                    backgroundColor: primaryColor,
                    boxShadow: `0 10px 25px -5px ${primaryColor}40`
                  } : {}}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Activating agents...</span>
                    </>
                  ) : (
                    <>
                      <span>Clarify with Agent</span>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {showIntentConfirm && (
            <div className={`mt-4 rounded-2xl border p-5 ${
              isDarkMode ? 'border-slate-700 bg-slate-800/60' : 'border-slate-200 bg-white'
            }`}>
              <div className={`mb-4 text-sm font-semibold uppercase tracking-wide ${
                isDarkMode ? 'text-emerald-300' : 'text-emerald-700'
              }`}>
                Agent King Intent Confirmation
              </div>

              <div className={`mb-4 rounded-xl p-3 text-sm ${
                isDarkMode ? 'bg-slate-900/60 text-slate-200' : 'bg-slate-50 text-slate-700'
              }`}>
                Before we launch matching, confirm the outcome so the request agent can target the right providers.
              </div>

              <div className="space-y-3">
                <div>
                  <label className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}>
                    Confirm your goal in one sentence
                  </label>
                  <input
                    value={intentSummary}
                    onChange={(e) => setIntentSummary(e.target.value)}
                    className={`w-full rounded-xl border px-4 py-2.5 text-sm outline-none focus:ring-2 ${
                      isDarkMode
                        ? 'border-slate-700 bg-slate-900/50 text-white focus:ring-indigo-500'
                        : 'border-slate-200 bg-slate-50 text-slate-900 focus:ring-indigo-500'
                    }`}
                    style={{ '--tw-ring-color': primaryColor }}
                  />
                </div>

                <div>
                  <label className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}>
                    Must-haves
                  </label>
                  <input
                    value={mustHaves}
                    onChange={(e) => setMustHaves(e.target.value)}
                    placeholder="e.g., verified provider, can start today, supplies included"
                    className={`w-full rounded-xl border px-4 py-2.5 text-sm outline-none focus:ring-2 ${
                      isDarkMode
                        ? 'border-slate-700 bg-slate-900/50 text-white focus:ring-indigo-500'
                        : 'border-slate-200 bg-slate-50 text-slate-900 focus:ring-indigo-500'
                    }`}
                    style={{ '--tw-ring-color': primaryColor }}
                  />
                </div>

                <div>
                  <label className={`mb-1 block text-xs font-semibold uppercase tracking-wide ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}>
                    Deal-breakers
                  </label>
                  <input
                    value={dealBreakers}
                    onChange={(e) => setDealBreakers(e.target.value)}
                    placeholder="e.g., no subcontractors, no weekend work, budget cap"
                    className={`w-full rounded-xl border px-4 py-2.5 text-sm outline-none focus:ring-2 ${
                      isDarkMode
                        ? 'border-slate-700 bg-slate-900/50 text-white focus:ring-indigo-500'
                        : 'border-slate-200 bg-slate-50 text-slate-900 focus:ring-indigo-500'
                    }`}
                    style={{ '--tw-ring-color': primaryColor }}
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowIntentConfirm(false)}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold ${
                    isDarkMode
                      ? 'border border-slate-600 bg-slate-800 text-slate-200'
                      : 'border border-slate-300 bg-white text-slate-700'
                  }`}
                >
                  Edit Request
                </button>
                <button
                  type="button"
                  onClick={submitConfirmedIntent}
                  disabled={isSubmitting || !intentSummary.trim()}
                  className={`rounded-xl px-5 py-2 text-sm font-semibold text-white ${
                    isSubmitting || !intentSummary.trim()
                      ? 'cursor-not-allowed bg-slate-400 opacity-60'
                      : ''
                  }`}
                  style={!(isSubmitting || !intentSummary.trim()) ? { backgroundColor: primaryColor } : {}}
                >
                  Confirm and Launch
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="mt-6 flex flex-col items-center">
          <div className={`flex items-center gap-2 text-sm ${
            isDarkMode ? 'text-slate-400' : 'text-slate-500'
          }`}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span>Agent-ranked providers and offers</span>
          </div>
          <div className={`mt-3 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            Examples: "Emergency plumber tonight" | "Private yoga instructor" | "Website redesign"
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestForm;
